import { Html5Qrcode } from 'html5-qrcode';
import type { ScannerState } from './types';

const state: ScannerState = {
  isScanning: false,
};

const resultInput = document.getElementById('result-input') as HTMLInputElement;
const uploadBtn = document.getElementById('upload-btn')!;
const fileInput = document.getElementById('file-input') as HTMLInputElement;
const openBtn = document.getElementById('open-btn') as HTMLButtonElement;
const copyBtn = document.getElementById('copy-btn') as HTMLButtonElement;
const clearBtn = document.getElementById('clear-btn') as HTMLButtonElement;
const statusElement = document.getElementById('status')!;
const readerElement = document.getElementById('reader')!;

let scanner: Html5Qrcode | null = null;

function sanitizeUrl(value: string): string | null {
  if (value.startsWith('javascript:') || value.startsWith('data:')) {
    return null;
  }
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }
  if (value.includes('.') && !value.includes(' ')) {
    return 'https://' + value;
  }
  return 'https://www.google.com/search?q=' + encodeURIComponent(value);
}

async function cleanupScanner(): Promise<void> {
  if (scanner && state.isScanning) {
    try {
      await scanner.stop();
    } catch {
      // scanner might already be stopped
    }
    state.isScanning = false;
  }
}

function setLoading(loading: boolean): void {
  if (loading) {
    readerElement.classList.add('loading');
  } else {
    readerElement.classList.remove('loading');
  }
}

function showRetryButton(): void {
  statusElement.innerHTML = `<span>Failed to activate camera</span><button id="retry-btn" class="btn btn-retry" aria-label="Retry camera">Retry</button>`;
  const retryBtn = document.getElementById('retry-btn')!;
  retryBtn.addEventListener('click', () => {
    initCamera();
  });
}

function showStatus(message: string, type: 'success' | 'error' | 'info'): void {
  statusElement.textContent = message;
  statusElement.className = `status ${type}`;
}

function enableButtons(): void {
  openBtn.disabled = false;
  copyBtn.disabled = false;
  clearBtn.disabled = false;
}

function disableButtons(): void {
  openBtn.disabled = true;
  copyBtn.disabled = true;
  clearBtn.disabled = true;
}

function onScanSuccess(decodedText: string): void {
  resultInput.value = decodedText;
  enableButtons();
  showStatus('Scan successful!', 'success');
}

function onScanError(_errorMessage: string): void {
  // Ignore - no barcode detected in current frame
}

async function initCamera(): Promise<void> {
  try {
    showStatus('Initializing camera...', 'info');
    setLoading(true);

    await cleanupScanner();

    scanner = new Html5Qrcode('reader');

    const cameras = await Html5Qrcode.getCameras();
    const backCamera = cameras.find(cam =>
      cam.label.toLowerCase().includes('back') ||
      cam.label.toLowerCase().includes('environment') ||
      cam.label.toLowerCase().includes('rear')
    ) || cameras[0];

    if (!backCamera) {
      throw new Error('Camera not found');
    }

    await scanner.start(
      backCamera.id,
      {
        fps: 15,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1,
        disableFlip: false,
      },
      onScanSuccess,
      onScanError
    );

    state.isScanning = true;
    disableButtons();
    setLoading(false);
    showStatus('Ready! Point camera at a barcode', 'info');

  } catch (error) {
    setLoading(false);
    const message = error instanceof Error ? error.message : 'Failed to activate camera';
    showRetryButton();
    console.error('Camera error:', error);
  }
}

async function scanFromFile(file: File): Promise<void> {
  try {
    showStatus('Processing image...', 'info');

    if (!scanner) {
      scanner = new Html5Qrcode('reader');
    }

    if (state.isScanning) {
      await scanner.stop();
      state.isScanning = false;
    }

    const result = await scanner.scanFile(file, true);

    resultInput.value = result;
    enableButtons();
    showStatus('Scan successful!', 'success');

  } catch (error) {
    showStatus('No barcode detected', 'error');
    console.error('File scan error:', error);

    clearBtn.disabled = false;
    openBtn.disabled = true;
    copyBtn.disabled = true;
  }
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for non-HTTPS contexts
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}

uploadBtn.addEventListener('click', () => {
  fileInput.value = '';
  fileInput.click();
});

fileInput.addEventListener('change', (e) => {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (file) {
    scanFromFile(file);
  }
});

openBtn.addEventListener('click', () => {
  const value = resultInput.value.trim();
  if (!value) return;

  const url = sanitizeUrl(value);
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    showStatus('Invalid URL detected', 'error');
  }
});

copyBtn.addEventListener('click', async () => {
  const value = resultInput.value.trim();
  if (value) {
    const success = await copyToClipboard(value);
    showStatus(success ? 'Copied to clipboard!' : 'Failed to copy', success ? 'success' : 'error');
  }
});

clearBtn.addEventListener('click', () => {
  resultInput.value = '';
  fileInput.value = '';

  disableButtons();
  showStatus('Ready! Point camera at a barcode', 'info');

  if (!state.isScanning && scanner) {
    initCamera();
  }
});

initCamera();

window.addEventListener('beforeunload', () => {
  if (scanner && state.isScanning) {
    scanner.stop().catch(() => {});
  }
});